/**
 * Build a plugin's embeddable-component registry entries from:
 *   1. manifest `uiComponents` (the curated list),
 *   2. `integrationPoints.dashboardWidgets` — merged into a matching entry
 *      (adding the dashboard panel id as an alias) or added as widgets,
 *   3. remaining `integrationPoints.pages` — unlisted views, so any page can
 *      still be embedded deliberately.
 *
 * Entries whose component can't be resolved are kept as `unresolved` (not
 * listed) so saved references render "unavailable" instead of vanishing.
 */

import type {
  EmbeddableComponentDescriptor,
  EmbeddableImplementation,
  UiComponentDeclaration,
} from '@neuro/plugin-sdk/ui';
import type { EmbedSdk } from './sdk-embed';
import { resolveComponentLoader } from './resolve-component-loader';

export interface BuildEmbeddablesInput {
  pluginId: string;
  manifest: Record<string, any> | null | undefined;
  integrationPoints: Record<string, any>;
  uiModule: Record<string, unknown> | null;
  /** ui/pages + ui/components loaders keyed by file name. */
  fileLoaders: Record<string, () => Promise<any>>;
  generation: number;
  /** Merge declared permissions with those implied by a route path. */
  resolvePermissions: (declared: string[] | undefined, path: string) => string[];
  sdk: Pick<EmbedSdk, 'normalizeUiComponentDeclaration' | 'extractRouteParams' | 'gridUnitsToPx' | 'parseEntrypoint'>;
}

export interface EmbeddableEntry {
  descriptor: EmbeddableComponentDescriptor;
  impl?: EmbeddableImplementation;
}

function componentNameOf(component: unknown): string | undefined {
  if (typeof component === 'string') return component;
  if (typeof component === 'function' && component.name) return component.name;
  return undefined;
}

export function buildEmbeddableDescriptors(input: BuildEmbeddablesInput): EmbeddableEntry[] {
  const { pluginId, manifest, integrationPoints, uiModule, fileLoaders, generation, resolvePermissions, sdk } = input;
  const pages: Array<{ path: string; component: unknown; title?: string }> = Array.isArray(integrationPoints?.pages)
    ? integrationPoints.pages.filter((p: any) => p && typeof p.path === 'string')
    : [];
  const allPagePaths = [...new Set(pages.map((p) => p.path))];
  const pathsByComponent = new Map<string, string[]>();
  for (const page of pages) {
    const name = componentNameOf(page.component);
    if (name) pathsByComponent.set(name, [...(pathsByComponent.get(name) ?? []), page.path]);
  }

  const routeFor = (exportName: string, explicit?: string) => {
    const candidates = pathsByComponent.get(exportName) ?? [];
    const pattern = explicit ?? candidates.find((p) => !p.includes(':')) ?? candidates[0];
    if (!pattern) return undefined;
    return { pattern, params: sdk.extractRouteParams(pattern), internalPatterns: allPagePaths.length ? allPagePaths : [pattern] };
  };

  const entries: EmbeddableEntry[] = [];
  const finish = (descriptor: EmbeddableComponentDescriptor, entrypoint: string): EmbeddableEntry => {
    const load = resolveComponentLoader(entrypoint, fileLoaders, uiModule);
    if (descriptor.kind === 'view') descriptor.route = routeFor(descriptor.exportName, descriptor.embed?.route);
    descriptor.requiredPermissions = resolvePermissions(descriptor.requiredPermissions, descriptor.route?.pattern ?? `/${pluginId}`);
    if (!load) {
      descriptor.status = 'unresolved';
      descriptor.listed = false;
      return { descriptor };
    }
    return { descriptor, impl: { load } };
  };

  // 1. Manifest uiComponents
  const declarations: UiComponentDeclaration[] = Array.isArray(manifest?.uiComponents)
    ? manifest!.uiComponents.filter((d: any) => d && typeof d.id === 'string' && typeof d.entrypoint === 'string')
    : [];
  const declaredSize = new Set<string>();
  for (const decl of declarations) {
    const descriptor = sdk.normalizeUiComponentDeclaration(pluginId, decl, { source: 'manifest', generation });
    if (entries.some((e) => e.descriptor.id === descriptor.id)) continue;
    if (decl.defaultSize) declaredSize.add(descriptor.id);
    entries.push(finish(descriptor, decl.entrypoint));
  }

  // 2. Dashboard widgets
  const widgets: any[] = Array.isArray(integrationPoints?.dashboardWidgets) ? integrationPoints.dashboardWidgets : [];
  for (const widget of widgets) {
    const component = componentNameOf(widget?.component);
    if (!widget?.id || !component) continue;
    const panelAlias = `${pluginId}:${widget.id}`;
    const size = sdk.gridUnitsToPx(widget.defaultSize);
    const existing = entries.find((e) => e.descriptor.exportName === sdk.parseEntrypoint(component).exportName);
    if (existing) {
      const d = existing.descriptor;
      if (panelAlias !== d.id && !d.aliases.includes(panelAlias)) d.aliases.push(panelAlias);
      if (size && !declaredSize.has(d.id)) d.defaultSize = size;
      if (Array.isArray(widget.requiredPermissions)) {
        d.requiredPermissions = [...new Set([...d.requiredPermissions, ...widget.requiredPermissions])];
      }
      continue;
    }
    const descriptor = sdk.normalizeUiComponentDeclaration(pluginId, {
      id: widget.id,
      entrypoint: component,
      title: typeof widget.title === 'string' ? widget.title : undefined,
      kind: 'widget',
      defaultSize: size,
      requiredPermissions: Array.isArray(widget.requiredPermissions) ? widget.requiredPermissions : undefined,
    }, { source: 'dashboardWidget', generation, extraAliases: [panelAlias] });
    if (entries.some((e) => e.descriptor.id === descriptor.id)) continue;
    entries.push(finish(descriptor, component));
  }

  // 3. Remaining pages → unlisted views
  const covered = new Set(entries.map((e) => e.descriptor.exportName));
  for (const [component, paths] of pathsByComponent) {
    if (covered.has(component) || typeof pages.find((p) => componentNameOf(p.component) === component)?.component !== 'string') continue;
    covered.add(component);
    const title = pages.find((p) => p.path === paths[0])?.title;
    const descriptor = sdk.normalizeUiComponentDeclaration(pluginId, {
      id: component,
      entrypoint: component,
      title: typeof title === 'string' ? title : component,
      kind: 'view',
      listed: false,
    }, { source: 'page', generation });
    entries.push(finish(descriptor, component));
  }

  return entries;
}
